"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = require("events");
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const builtInRules = __importStar(require("./built-in-rules"));
const find_first_1 = __importDefault(require("./find-first"));
class RulesEngine extends events_1.EventEmitter {
    constructor({ plugins, immutable, onLoadSetting } = {}) {
        super();
        this._ruleTypes = Object.assign({}, builtInRules, ...(plugins || []).map(plugin => plugin.rules || {}));
        this._immutable = Boolean(immutable);
        this._tokenCache = {};
        this._onLoadSetting = onLoadSetting !== null && onLoadSetting !== void 0 ? onLoadSetting : (async () => null);
    }
    testRule(rule, { params, fqAppId, key, parentRefs: referrers = [] }) {
        try {
            this.emit('testing');
            if (!rule) {
                return false;
            }
            const ruleType = this._ruleTypes[rule.type];
            if (!ruleType) {
                return false;
            }
            const result = ruleType.test({
                rule,
                params,
                fqAppId,
                key,
                ruleTypes: this._ruleTypes,
                resolveValue: async (fqAppId, setting, customParams) => {
                    if (referrers === null || referrers === void 0 ? void 0 : referrers.some(r => r.fqAppId === fqAppId && r.setting === setting)) {
                        this.emit('testError', new Error('Circular reference detected'));
                        return null;
                    }
                    else {
                        const resolvedSetting = await this._onLoadSetting(fqAppId, setting);
                        if (!resolvedSetting) {
                            return null;
                        }
                        return this.resolveValue(resolvedSetting, customParams !== null && customParams !== void 0 ? customParams : params, referrers.concat({ fqAppId, setting }));
                    }
                }
            });
            this.emit('tested');
            return result;
        }
        catch (err) {
            this.emit('testError', err);
            return false;
        }
    }
    resolveValue(setting, params, parentRefs = []) {
        const { fqAppId, setting: key, rules } = setting;
        if ('value' in rules) {
            return this.maybeClone(rules.value);
        }
        const choices = rules.values || [];
        return this.findMatchingValue(choices, { fqAppId, params, key, parentRefs });
    }
    async resolveValues(settings, params, propertyPath = '') {
        const promises = [];
        const resultTree = settings.reduce((resultTree, setting) => {
            const { setting: key } = setting;
            if (key.indexOf(propertyPath) !== 0 &&
                propertyPath.indexOf(key) !== 0) {
                return resultTree;
            }
            const pathComponents = this.tokenizeKey(key);
            pathComponents.reduce((target, key, i) => {
                if (i === pathComponents.length - 1) {
                    if (!target || typeof target !== 'object') {
                        this.emit('outputError', new Error(`Setting ${key} could not be merged into results tree`));
                    }
                    else {
                        const value = this.resolveValue(setting, params);
                        if (value instanceof Promise) {
                            // Defer the promises so they can be resolved in parallel
                            promises.push(new Promise((resolve) => {
                                value.then(val => {
                                    target[key] = val;
                                    resolve();
                                });
                            }));
                        }
                        else {
                            target[key] = value;
                        }
                    }
                }
                else {
                    if (!(key in target)) {
                        target[key] = {};
                    }
                    return target[key];
                }
            }, resultTree);
            return resultTree;
        }, {});
        await Promise.all(promises);
        // key can be a dotted property path; walk through the properties if so
        return propertyPath
            ? this.tokenizeKey(propertyPath)
                .reduce((settings, prop) => settings && settings[prop], resultTree)
            : resultTree;
    }
    findMatchingValue(choices, evalContext) {
        // We can't do a simple find because of a subtle, inconveniently allowed
        // behavior. Multiple rules can match, and if one is marked as `isDefault`,
        // we prefer that choice, otherwise we choose the first one. To allow
        // short-circuiting rule evaluation, we'll simply skip evaluating rules
        // after finding a match unless a candidate is marked as `isDefault`
        let match;
        for (const choice of choices) {
            const needToEvaluate = !match || choice.isDefault;
            if (needToEvaluate) {
                const testRuleResult = this.testRule(choice.rule, evalContext);
                // If any of the tests return a promise then the above optimization is
                // thrown out the window. We need to resolve each choice in order to
                // determine the matching value.
                if (testRuleResult instanceof Promise) {
                    return this.findMatchingValueAsync(choices, evalContext);
                }
                if (testRuleResult) {
                    match = choice;
                }
            }
        }
        match = match ||
            (0, find_first_1.default)(choices, choice => choice.isDefault === true) ||
            choices[0];
        return match && this.maybeClone(match.value);
    }
    findMatchingValueAsync(choices, evalContext) {
        return new Promise(async (resolve) => {
            const ruleResults = await Promise.all(choices.map(choice => this.testRule(choice.rule, evalContext)));
            const match = choices.find(({ isDefault }, i) => isDefault && ruleResults[i]) ||
                choices.find((_, i) => ruleResults[i]) ||
                choices.find(({ isDefault }) => isDefault) ||
                choices[0];
            resolve(match && this.maybeClone(match.value));
        });
    }
    tokenizeKey(key) {
        if (!(key in this._tokenCache)) {
            const tokens = [];
            let escapeSeq = false;
            let currentToken = '';
            for (let i = 0; i < key.length; i++) {
                const char = key[i];
                if (escapeSeq) {
                    currentToken += char;
                    escapeSeq = false;
                }
                else if (char === '.') {
                    tokens.push(currentToken);
                    currentToken = '';
                }
                else if (char === '\\') {
                    escapeSeq = true;
                }
                else {
                    currentToken += char;
                }
            }
            tokens.push(currentToken);
            this._tokenCache[key] = tokens;
        }
        return this._tokenCache[key];
    }
    maybeClone(value) {
        return this._immutable ? value : (0, cloneDeep_1.default)(value);
    }
}
exports.default = RulesEngine;
